/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Admin Feedback Detail Modal - View and respond to feedback (Admin only)
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useEffect, useRef } from 'react'
import {
  X,
  Calendar,
  Image as ImageIcon,
  CheckCircle,
  Send,
  AlertCircle,
  User,
  Github,
  ExternalLink,
  FileText,
  Download
} from 'lucide-react'
import ModalPortal from '../ModalPortal'
import useFeedbackStore from '../../stores/feedbackStore'
import feedbackService from '../../services/feedbackService'
import FeedbackCategoryBadge from './FeedbackCategoryBadge'
import FeedbackStatusBadge from './FeedbackStatusBadge'
import FeedbackRatingDisplay from './FeedbackRatingDisplay'
import { stripHTML } from '../../utils/sanitization'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AdminFeedbackDetailModal')

const AdminFeedbackDetailModal = () => {
  const { user } = useAuthStore()
  const { isDetailModalOpen, selectedFeedback, closeDetailModal, updateFeedbackInList } =
    useFeedbackStore()

  // Response form state
  const [responseText, setResponseText] = useState('')
  const [newStatus, setNewStatus] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [isCreatingIssue, setIsCreatingIssue] = useState(false)
  const [errors, setErrors] = useState({})

  // Track previous modal state to detect when it opens
  const prevModalOpenRef = useRef(false)

  // Reset form only when modal opens (not when selectedFeedback changes while modal is open)
  useEffect(() => {
    const wasModalClosed = !prevModalOpenRef.current
    const isModalNowOpen = isDetailModalOpen

    if (isModalNowOpen && wasModalClosed && selectedFeedback) {
      // Modal just opened, reset form with selected feedback data
      setResponseText(selectedFeedback.adminResponse || '')
      setNewStatus(selectedFeedback.status || 'pending')
      setErrors({})
    }

    // Update ref for next render
    prevModalOpenRef.current = isDetailModalOpen
  }, [isDetailModalOpen, selectedFeedback])

  if (!isDetailModalOpen || !selectedFeedback) return null

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const validateResponse = () => {
    const newErrors = {}

    if (!responseText.trim()) {
      newErrors.response = 'Response is required'
    } else if (responseText.trim().length < 10) {
      newErrors.response = 'Response must be at least 10 characters'
    } else if (responseText.trim().length > 1000) {
      newErrors.response = 'Response must be less than 1000 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmitResponse = async () => {
    if (!validateResponse()) {
      return
    }

    setIsSubmitting(true)

    try {
      const data = {
        response: responseText.trim(),
        status: newStatus
      }

      const updatedFeedback = await feedbackService.respondToFeedback(selectedFeedback.id, data)

      // Update the feedback in the store
      updateFeedbackInList(selectedFeedback.id, {
        adminResponse: updatedFeedback.adminResponse,
        status: updatedFeedback.status,
        respondedAt: updatedFeedback.respondedAt,
        respondedBy: updatedFeedback.respondedBy
      })

      toast.success('Response sent successfully!')
    } catch (error) {
      logger.error('Error submitting response:', error)
      const errorMessage = error.response?.data?.error || 'Failed to submit response'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateStatus = async (status) => {
    setIsUpdatingStatus(true)

    try {
      await feedbackService.updateFeedbackStatus(selectedFeedback.id, status)

      // Update the feedback in the store
      updateFeedbackInList(selectedFeedback.id, { status })
      setNewStatus(status)

      toast.success('Status updated successfully!')
    } catch (error) {
      logger.error('Error updating status:', error)
      const errorMessage = error.response?.data?.error || 'Failed to update status'
      toast.error(errorMessage)
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleCreateGitHubIssue = async () => {
    if (selectedFeedback.githubIssueUrl) {
      // Already has issue - open in new tab
      window.open(selectedFeedback.githubIssueUrl, '_blank')
      return
    }

    setIsCreatingIssue(true)

    try {
      const result = await feedbackService.createGitHubIssue(selectedFeedback.id)

      // Update the feedback in the store
      updateFeedbackInList(selectedFeedback.id, {
        githubIssueUrl: result.feedback.githubIssueUrl,
        githubIssueCreatedAt: result.feedback.githubIssueCreatedAt
      })

      toast.success('GitHub issue created successfully!')
    } catch (error) {
      logger.error('Error creating GitHub issue:', error)
      const errorData = error.response?.data?.error
      const errorMessage =
        typeof errorData === 'string'
          ? errorData
          : errorData?.message || 'Failed to create GitHub issue'
      toast.error(errorMessage)
    } finally {
      setIsCreatingIssue(false)
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeDetailModal} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Feedback Details (Admin View)
            </h2>
            <button
              onClick={closeDetailModal}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* User Information */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Submitted By
              </h3>
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <User className="w-8 h-8 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedFeedback.user?.firstName && selectedFeedback.user?.lastName
                      ? `${selectedFeedback.user.firstName} ${selectedFeedback.user.lastName}`
                      : selectedFeedback.user?.name ||
                        selectedFeedback.user?.email ||
                        'Unknown User'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedFeedback.user?.email || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Badges and Rating */}
            <div className="flex flex-wrap items-center gap-3">
              <FeedbackCategoryBadge category={selectedFeedback.category} />
              <FeedbackStatusBadge status={selectedFeedback.status} />
              {selectedFeedback.screenshot && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                  <ImageIcon className="w-4 h-4" />
                  Screenshot Attached
                </span>
              )}
            </div>

            {selectedFeedback.rating && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  User Rating
                </h3>
                <FeedbackRatingDisplay rating={selectedFeedback.rating} showNumber />
              </div>
            )}

            {/* Message */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Feedback Message
              </h3>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                  {stripHTML(selectedFeedback.message)}
                </p>
              </div>
            </div>

            {/* Screenshot */}
            {selectedFeedback.screenshot && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Screenshot
                </h3>
                <div className="rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  <a
                    href={selectedFeedback.screenshot}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:opacity-90 transition-opacity"
                  >
                    <img
                      src={selectedFeedback.screenshot}
                      alt="Feedback screenshot"
                      className="w-full h-auto cursor-pointer"
                      onError={(e) => {
                        logger.error('Failed to load screenshot:', selectedFeedback.screenshot)
                        e.target.src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5TY3JlZW5zaG90IG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+'
                      }}
                    />
                  </a>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 px-2 pb-2">
                    Click to open in new tab
                  </p>
                </div>
              </div>
            )}

            {/* Context Information */}
            {(selectedFeedback.challenge ||
              selectedFeedback.stage ||
              selectedFeedback.question) && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Context
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-2 text-sm">
                  {selectedFeedback.challenge && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Challenge:{' '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {selectedFeedback.challenge.name}
                      </span>
                    </div>
                  )}
                  {selectedFeedback.stage && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Stage: </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {selectedFeedback.stage.name}
                      </span>
                    </div>
                  )}
                  {selectedFeedback.question && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Question ID:{' '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {selectedFeedback.question.question_id || selectedFeedback.question.id}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Attached Score Report (auto-created tickets from failed PDF uploads) */}
            {selectedFeedback.scoreReportId && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attached Score Report
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="font-mono text-xs">{selectedFeedback.scoreReportId}</span>
                  </div>
                  <a
                    href={`/api/score-reports/admin/${selectedFeedback.scoreReportId}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download uploaded PDF
                  </a>
                </div>
              </div>
            )}

            {/* Metadata */}
            {selectedFeedback.metadata && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Technical Metadata
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <pre className="text-xs text-gray-900 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                    {(() => {
                      // Sanitize metadata: only show safe, non-executable fields
                      const safeMetadata = {}
                      const allowedFields = [
                        // Build info
                        'uiBuildId',
                        'apiBuildId',
                        // Browser info
                        'browser',
                        'browserVersion',
                        'platform',
                        'language',
                        'timezone',
                        // Screen info
                        'screenWidth',
                        'screenHeight',
                        'screenResolution',
                        'viewportSize',
                        'devicePixelRatio',
                        'colorDepth',
                        'pixelRatio',
                        // Page info
                        'url',
                        'pathname',
                        'referrer',
                        'currentUrl',
                        // Device info
                        'connectionType',
                        'deviceMemory',
                        // Timestamp
                        'timestamp'
                      ]

                      if (
                        selectedFeedback.metadata &&
                        typeof selectedFeedback.metadata === 'object'
                      ) {
                        allowedFields.forEach((field) => {
                          if (field in selectedFeedback.metadata) {
                            // Convert to string and limit length to prevent display issues
                            const value = selectedFeedback.metadata[field]
                            if (typeof value === 'string' && value.length > 500) {
                              safeMetadata[field] = value.substring(0, 500) + '... (truncated)'
                            } else {
                              safeMetadata[field] = value
                            }
                          }
                        })
                      }

                      return JSON.stringify(safeMetadata, null, 2)
                    })()}
                  </pre>
                </div>
              </div>
            )}

            {/* Quick Status Update */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quick Status Update
              </h3>
              <div className="flex flex-wrap gap-2">
                {['pending', 'in_review', 'resolved', 'closed'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleUpdateStatus(status)}
                    disabled={selectedFeedback.status === status || isUpdatingStatus}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-colors
                      ${
                        selectedFeedback.status === status
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 cursor-default'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {status.replace('_', ' ').charAt(0).toUpperCase() +
                      status.replace('_', ' ').slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* GitHub Issue Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                GitHub Issue
              </h3>
              <div className="flex items-center gap-3">
                {selectedFeedback.githubIssueUrl ? (
                  <>
                    <a
                      href={selectedFeedback.githubIssueUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      <Github className="w-5 h-5" />
                      View Issue
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    {selectedFeedback.githubIssueCreatedAt && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Created {formatDate(selectedFeedback.githubIssueCreatedAt)}
                      </span>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleCreateGitHubIssue}
                    disabled={isCreatingIssue}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-400 dark:disabled:bg-gray-500 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                  >
                    {isCreatingIssue ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating Issue...
                      </>
                    ) : (
                      <>
                        <Github className="w-5 h-5" />
                        Create GitHub Issue
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Admin Response Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {selectedFeedback.adminResponse ? 'Update Response' : 'Respond to Feedback'}
              </h3>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                rows={6}
                className={`
                  w-full px-4 py-3 rounded-lg border
                  ${
                    errors.response
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                  }
                  dark:bg-gray-700 dark:text-gray-100
                  focus:outline-none focus:ring-2
                  resize-none
                `}
                placeholder="Write your response here... (10-1000 characters)"
              />
              <div className="flex justify-between items-center mt-2">
                {errors.response && (
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.response}
                  </p>
                )}
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-auto">
                  {responseText.length}/1000
                </span>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="pending">Pending</option>
                  <option value="in_review">In Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>

                <button
                  onClick={handleSubmitResponse}
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Response
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Previous Response (if exists) */}
            {selectedFeedback.adminResponse && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Previous Response
                  </h3>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap mb-3">
                    {stripHTML(selectedFeedback.adminResponse)}
                  </p>
                  {selectedFeedback.respondedAt && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Responded on {formatDate(selectedFeedback.respondedAt)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Submitted: </span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatDate(selectedFeedback.createdAt)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    Last Updated:{' '}
                  </span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatDate(selectedFeedback.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <button
              onClick={closeDetailModal}
              className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}

export default AdminFeedbackDetailModal
